/**
 * Teams API Service
 * Handles fetching team data from the backend
 */

import { get, post, patch, put, del } from "./api";
import type {
  TeamResponse,
  TeamSearchResult,
  TeamRosterImageResponse,
  CreateTeamWithPlayersRequest,
  CreateTeamWithPlayersResponse,
  UpdateProspectTeamRequest,
  UpdateTeamRequest,
  AddProspectToTeamResponse,
  BulkAddProspectsToTeamResponse,
  CreateTeamResponse,
  TeamInfo,
  ManageableTeamResponse,
  DeletedTeamResponse,
  BulkTeamActionResponse
} from "../types/api";

export const getTeams = async (): Promise<TeamResponse[]> => {
  return get<TeamResponse[]>("/api/teams");
};

/** Team ids the current user follows (per-user, org-scoped). */
export const getFollowedTeams = async (): Promise<string[]> => {
  return get<string[]>("/api/teams/followed");
};

/** Follow a team for the current user. Idempotent server-side. */
export const followTeam = async (teamId: string): Promise<void> => {
  await post<void>(`/api/teams/${teamId}/follow`, undefined);
};

/** Unfollow a team for the current user. */
export const unfollowTeam = async (teamId: string): Promise<void> => {
  await del(`/api/teams/${teamId}/follow`);
};

/** Raw names the current user follows by name (no org team created), org-scoped. */
export const getFollowedTeamNames = async (): Promise<string[]> => {
  return get<string[]>("/api/teams/followed-names");
};

/**
 * Follow a team by its raw name — creates no org team. Idempotent server-side; the
 * followed team's games surface in the itinerary by matching game raw names.
 */
export const followTeamByName = async (name: string): Promise<void> => {
  await post<void>("/api/teams/follow-by-name", { name });
};

/** Unfollow a name-only team for the current user. */
export const unfollowTeamByName = async (name: string): Promise<void> => {
  await del(`/api/teams/follow-by-name?name=${encodeURIComponent(name)}`);
};

export const searchTeams = async (searchTerm: string): Promise<TeamSearchResult[]> => {
  const queryParams = new URLSearchParams({ searchTerm });
  return get<TeamSearchResult[]>(`/api/teams/search?${queryParams.toString()}`);
};

export const createTeamWithPlayers = async (
  data: CreateTeamWithPlayersRequest
): Promise<CreateTeamWithPlayersResponse> => {
  return post<CreateTeamWithPlayersResponse>('/api/Teams', data);
};

/**
 * Resolve a team's canonical roster image to a freshly-signed, short-lived
 * download URL. Throws an `ApiError` with `statusCode === 404` when the team
 * has no roster image (or its GCS object is gone) — callers should treat that
 * as the "empty" state, not a hard error.
 */
export const getTeamRosterImage = async (
  teamId: string
): Promise<TeamRosterImageResponse> => {
  return get<TeamRosterImageResponse>(`/api/teams/${teamId}/roster-image`);
};

/**
 * Create a new team (or return existing team if name already exists)
 */
export const createTeam = async (
  name: string
): Promise<CreateTeamResponse> => {
  return post<CreateTeamResponse>('/api/teams/create', { name });
};

/**
 * Update team name (rename team)
 */
export const updateTeam = async (
  teamId: string,
  data: UpdateTeamRequest
): Promise<TeamResponse> => {
  return put<TeamResponse>(`/api/teams/${teamId}`, data);
};

/**
 * Add an existing prospect to a team
 */
export const addProspectToTeam = async (
  teamId: string,
  prospectId: string
): Promise<AddProspectToTeamResponse> => {
  return post<AddProspectToTeamResponse>(`/api/teams/${teamId}/prospects/${prospectId}`, {});
};

/**
 * Bulk-add existing prospects to a team in a single submission.
 * Already-associated, missing, or cross-org prospects are bucketed in the
 * response rather than failing the batch. Max 500 per call.
 */
export const bulkAddProspectsToTeam = async (
  teamId: string,
  prospectIds: string[]
): Promise<BulkAddProspectsToTeamResponse> => {
  return post<BulkAddProspectsToTeamResponse>(
    `/api/teams/${teamId}/prospects/bulk`,
    { prospectIds }
  );
};

export const updateProspectTeam = async (
  teamId: string,
  prospectId: string,
  data: UpdateProspectTeamRequest
): Promise<TeamInfo> => {
  return patch<TeamInfo>(`/api/teams/${teamId}/prospects/${prospectId}`, data);
};

/**
 * Delete a team (soft delete)
 * Note: This will NOT delete prospects on the team, only the team itself.
 */
export const deleteTeam = async (teamId: string): Promise<void> => {
  return del<void>(`/api/teams/${teamId}`);
};

/**
 * List the org's live teams for bulk management, each with its player count
 * (zero marks a cleanup candidate) and linked-game count. Org scoping is
 * enforced server-side.
 */
export const getManageableTeams = async (): Promise<ManageableTeamResponse[]> => {
  return get<ManageableTeamResponse[]>("/api/teams/manage");
};

/**
 * Soft-delete the given teams, rosters and all. The server re-verifies each id
 * is in the caller's org and still live, and reports which ids it acted on vs.
 * skipped. Reversible via `restoreTeams`.
 */
export const bulkDeleteTeams = async (
  teamIds: string[]
): Promise<BulkTeamActionResponse> => {
  return post<BulkTeamActionResponse>("/api/teams/bulk-delete", { teamIds });
};

/** List the org's soft-deleted teams, most recently deleted first. */
export const getDeletedTeams = async (): Promise<DeletedTeamResponse[]> => {
  return get<DeletedTeamResponse[]>("/api/teams/deleted");
};

/**
 * Restore the given soft-deleted teams along with the roster links and notes
 * their delete took. Ids that are not restorable come back in `skippedIds`.
 */
export const restoreTeams = async (
  teamIds: string[]
): Promise<BulkTeamActionResponse> => {
  return post<BulkTeamActionResponse>("/api/teams/bulk-restore", { teamIds });
};

/**
 * Remove a prospect from a team (soft delete)
 */
export const removeProspectFromTeam = async (
  teamId: string,
  prospectId: string
): Promise<void> => {
  return del<void>(`/api/teams/${teamId}/prospects/${prospectId}`);
};
